/**
 * Bingo Card Creation Wizard
 *
 * Multi-step wizard for creating a new bingo card.
 */

import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Search } from 'lucide-react';
import type { CreateBingoCardInput, CreateBingoCellInput } from '@/types/bingo';
import type { Goal } from '@/types';
import { useCreateBingoCard, useCreateBingoCell } from '@/hooks/use-bingo';
import { useGoals } from '@/hooks/use-goals';
import { Button, Input } from '@/components/ui';

interface BingoCardCreateProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (cardId: string) => void;
}

type Step = 'card' | 'cells' | 'review';

export function BingoCardCreate({ isOpen, onClose, onSuccess }: BingoCardCreateProps) {
  const [step, setStep] = useState<Step>('card');
  const [cardData, setCardData] = useState<CreateBingoCardInput>({
    title: '',
    year: new Date().getFullYear(),
    gridSize: 5,
  });
  const [cells, setCells] = useState<CreateBingoCellInput[]>([]);
  const [goalSearch, setGoalSearch] = useState('');
  const [selectedGoals, setSelectedGoals] = useState<Map<number, Goal>>(new Map());
  
  const createCardMutation = useCreateBingoCard();
  const createCellMutation = useCreateBingoCell();
  const { data: goalsData } = useGoals({ status: 'active' });
  const availableGoals = goalsData?.goals || [];

  if (!isOpen) return null;

  const handleCardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardData.title.trim()) return;
    
    // Initialize cells array
    const gridSize = cardData.gridSize;
    const newCells: CreateBingoCellInput[] = [];
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        newCells.push({
          goalTitle: '',
          row,
          column: col,
          status: 'not_started',
        });
      }
    }
    setCells(newCells);
    setStep('cells');
  };

  const handleCellChange = (index: number, field: keyof CreateBingoCellInput, value: string | undefined) => {
    const newCells = [...cells];
    if (value === undefined) {
      const { [field]: _, ...rest } = newCells[index];
      newCells[index] = rest as CreateBingoCellInput;
    } else {
      newCells[index] = { ...newCells[index], [field]: value };
    }
    setCells(newCells);
  };

  const handleCreate = async () => {
    try {
      // Create card
      const card = await createCardMutation.mutateAsync(cardData);
      
      // Create cells
      for (const cell of cells) {
        if (cell.goalTitle.trim()) {
          await createCellMutation.mutateAsync({
            cardId: card.id,
            input: cell,
          });
        }
      }
      
      onSuccess?.(card.id);
      onClose();
      // Reset
      setStep('card');
      setCardData({ title: '', year: new Date().getFullYear(), gridSize: 5 });
      setCells([]);
    } catch (error) {
      console.error('Error creating bingo card:', error);
    }
  };

  const gridSize = cardData.gridSize;
  const gridCols = {
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Create Goal Bingo Card</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Step 1: Card Details */}
            {step === 'card' && (
              <form onSubmit={handleCardSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Title
                  </label>
                  <Input
                    value={cardData.title}
                    onChange={(e) => setCardData({ ...cardData, title: e.target.value })}
                    placeholder="e.g., 2026 Goals"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <Input
                    type="number"
                    value={cardData.year}
                    onChange={(e) => setCardData({ ...cardData, year: parseInt(e.target.value) })}
                    min={2020}
                    max={2100}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grid Size ({gridSize}x{gridSize})
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="7"
                    value={gridSize}
                    onChange={(e) => setCardData({ ...cardData, gridSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>3x3</span>
                    <span>4x4</span>
                    <span>5x5</span>
                    <span>6x6</span>
                    <span>7x7</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit">
                    Next: Add Goals
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>
            )}

            {/* Step 2: Add Cells */}
            {step === 'cells' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Add Goals to Your Card</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Fill in the goals for each cell. You can type a new goal or select from existing goals.
                  </p>
                  
                  {/* Goal search/select */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={goalSearch}
                        onChange={(e) => setGoalSearch(e.target.value)}
                        placeholder="Search existing goals..."
                        className="pl-10"
                      />
                    </div>
                    {goalSearch && (
                      <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                        {availableGoals
                          .filter((goal) =>
                            goal.title.toLowerCase().includes(goalSearch.toLowerCase())
                          )
                          .slice(0, 5)
                          .map((goal) => (
                            <button
                              key={goal.id}
                              type="button"
                              onClick={() => {
                                const emptyIndex = cells.findIndex((c) => !c.goalTitle.trim());
                                if (emptyIndex >= 0) {
                                  handleCellChange(emptyIndex, 'goalTitle', goal.title);
                                  handleCellChange(emptyIndex, 'goalId', goal.id);
                                  setSelectedGoals(new Map(selectedGoals.set(emptyIndex, goal)));
                                  setGoalSearch('');
                                }
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                            >
                              {goal.title}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`grid ${gridCols[gridSize as keyof typeof gridCols]} gap-2`}>
                  {cells.map((cell, index) => (
                    <div key={index} className="space-y-1">
                      <Input
                        value={cell.goalTitle || ''}
                        onChange={(e) => {
                          handleCellChange(index, 'goalTitle', e.target.value);
                          // Clear goalId if title is manually changed
                          if (selectedGoals.has(index)) {
                            const newSelected = new Map(selectedGoals);
                            newSelected.delete(index);
                            setSelectedGoals(newSelected);
                            handleCellChange(index, 'goalId', undefined);
                          }
                        }}
                        placeholder={`Goal ${index + 1}`}
                        className="text-xs"
                      />
                      {selectedGoals.has(index) && (
                        <span className="text-xs text-blue-600">Linked to Goal</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('card')}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={() => setStep('review')}>
                    Review
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 'review' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Review Your Card</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p><strong>Title:</strong> {cardData.title}</p>
                    <p><strong>Year:</strong> {cardData.year}</p>
                    <p><strong>Grid Size:</strong> {gridSize}x{gridSize}</p>
                    <p><strong>Goals Added:</strong> {cells.filter(c => c.goalTitle.trim()).length} / {cells.length}</p>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('cells')}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={createCardMutation.isPending || createCellMutation.isPending}
                  >
                    {createCardMutation.isPending || createCellMutation.isPending
                      ? 'Creating...'
                      : 'Create Card'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

